import { useCallback, useMemo, useState } from 'react'

import {
  ActionIcon,
  Button,
  Center,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core'
import { TimeInput } from '@mantine/dates'
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react'
import produce from 'immer'
import { useTranslate } from 'react-admin'
import { v4 as uuidv4 } from 'uuid'
import { VideoPromotionHelper } from '../../../../helpers/VideoPromotion.helper'
import { useVideoPromotionStore } from '../../../../stores/useVideoPromotionStore'
import { IPromotionInfoCard } from '../../../../types/components/VideoPromotion.type'
import { LXTable, LXTableColumn } from '../../../LXTable/LXTable'
import { EditInfocardTarget } from './EditInfocardTarget'

type LXInfocardItemTableType = {
  id: string
  target: JSX.Element
  customMessage: JSX.Element
  teaserText: JSX.Element
  teaserStartOffsetSeconds: JSX.Element

  actions: JSX.Element
}

export const PromotionDefineCards = () => {
  const t = useTranslate()
  const videoPromotionStore = useVideoPromotionStore()

  const infoCards = videoPromotionStore.infoCardsDraft
  const setInfocards = useCallback((recipe: (draft: IPromotionInfoCard[]) => void) => {
    const { infoCardsDraft, setInfoCardsDraft } = useVideoPromotionStore.getState()
    setInfoCardsDraft(produce(infoCardsDraft, recipe))
  }, [])
  const [selectedInfocard, setSelectedInfocard] = useState<IPromotionInfoCard | undefined>()

  const targetLabel = useCallback(
    (ic: IPromotionInfoCard): string | undefined => {
      if (!ic.target.title) {
        return undefined
      }
      const typeLabel = t(`pages.videoPromotion.infoCards.types.${ic.target.type}`)
      return `${typeLabel} : ${ic.target.title}`
    },
    [t],
  )

  const columns: LXTableColumn<LXInfocardItemTableType>[] = useMemo(
    () => [
      { key: 'id', value: '#', size: 'xs' },
      { key: 'target', value: t('_generics.target'), type: 'component', size: 'sm' },
      {
        key: 'customMessage',
        value: t('pages.videoPromotion.infoCards.customMessage'),
        type: 'component',
        size: 'md',
      },
      {
        key: 'teaserText',
        value: t('pages.videoPromotion.infoCards.teaserText'),
        type: 'component',
        size: 'md',
      },
      {
        key: 'teaserStartOffsetSeconds',
        value: t('pages.videoPromotion.infoCards.teaserStartOffsetSeconds'),
        type: 'component',
      },
      {
        key: 'actions',
        value: '',
        type: 'component',
      },
    ],
    [t],
  )

  const onRemoveInfocard = useCallback(
    (id: IPromotionInfoCard['id']) => {
      setInfocards(ics => {
        const icIndex = ics.findIndex(ic => ic.id === id)
        if (icIndex >= 0) {
          ics.splice(icIndex, 1)
        }
      })
    },
    [setInfocards],
  )

  const data = useMemo(
    () =>
      infoCards.map((ic, idx) => ({
        id: (idx + 1).toString(),
        target: (
          <Group wrap="nowrap">
            <ActionIcon variant="subtle" onClick={() => setSelectedInfocard(ic)}>
              <IconPencil size={16} />
            </ActionIcon>
            <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
              {targetLabel(ic) ?? (
                <Text size="sm" c="dimmed" fs="italic">
                  {t('pages.videoPromotion.infoCards.targetNotSet')}
                </Text>
              )}
            </Text>
          </Group>
        ),
        customMessage: (
          <Textarea
            value={ic.customMessage}
            placeholder={t('_generics.optional')}
            onChange={event =>
              setInfocards(ics => {
                ics[idx].customMessage = event.currentTarget.value
              })
            }
          />
        ),
        teaserText: (
          <Textarea
            value={ic.teaserText}
            placeholder={t('_generics.optional')}
            onChange={event =>
              setInfocards(ics => {
                ics[idx].teaserText = event.currentTarget.value
              })
            }
          />
        ),
        teaserStartOffsetSeconds: (
          <TimeInput
            value={ic.teaserStartOffsetSeconds}
            defaultValue="00:00:00"
            onChange={event =>
              setInfocards(ics => {
                ics[idx].teaserStartOffsetSeconds = event.currentTarget.value
              })
            }
            withSeconds
          />
        ),
        actions: (
          <ActionIcon
            variant="subtle"
            color="red"
            aria-label="Delete"
            onClick={() => onRemoveInfocard(ic.id)}
          >
            <IconTrash size={16} />
          </ActionIcon>
        ),
      })),
    [infoCards, setInfocards, t, onRemoveInfocard, targetLabel],
  )

  const onAddCard = useCallback(() => {
    setInfocards(ic => {
      ic.push({
        ...VideoPromotionHelper.DEFAULT_INFOCARD,
        id: uuidv4(),
      })
    })
  }, [setInfocards])

  const onClose = () => {
    setSelectedInfocard(undefined)
  }

  return (
    <Stack mt="xl" gap="xs">
      <Group gap="sm" align="center">
        <Title order={5}>{t('pages.videoPromotion.infoCards.cardToApply')}</Title>
        <Text size="xs" c="dimmed" fs="italic" pt={7}>
          {t('pages.videoPromotion.infoCards.max5Cards')}
        </Text>
      </Group>
      <Stack>
        <LXTable<LXInfocardItemTableType> items={data} columns={columns} />
        <Center>
          <Button
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={onAddCard}
            disabled={infoCards.length > 4}
          >
            {t('pages.videoPromotion.infoCards.addCard')}
          </Button>
        </Center>
      </Stack>
      <Modal
        title={<Title size="h3">{t('pages.videoPromotion.infoCards.editInfocardTarget')}</Title>}
        opened={!!selectedInfocard}
        onClose={onClose}
        size="lg"
      >
        <EditInfocardTarget infocardId={selectedInfocard?.id} onClose={onClose} />
      </Modal>
    </Stack>
  )
}
